import { useEffect, useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import {
  MagnifyingGlass, FileText, Lightning, TrendUp, Briefcase, Clock,
  ArrowRight, ChartBar, Buildings, Bell,
  Rocket, Target, ArrowSquareOut, Gear, User, Timer, Percent
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, AreaChart, Area } from "recharts";

export default function Dashboard({ setView }) {
  const {
    stats, searchHistory, profile, fetchStats, fetchSearchHistory,
    fetchJobs, fetchAutoApplyCandidates, fetchAutomationStatus,
    fetchScoreDistribution, totalJobsCount,
    activePersona, setSelectedJob,
  } = useApp();
  const [autoApplyCount, setAutoApplyCount] = useState(0);
  const [automation, setAutomation] = useState(null);
  const [scoreDistribution, setScoreDistribution] = useState([]);
  const [topJobs, setTopJobs] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchSearchHistory();
    // Fetch top 6 jobs for top matches panel
    fetchJobs({ sort_by: "score" }, 1).then(data => {
      if (data?.jobs) setTopJobs(data.jobs.filter(j => j.scored).slice(0, 6));
    });
    fetchAutoApplyCandidates().then(d => setAutoApplyCount(d?.count || 0));
    fetchAutomationStatus().then(s => s && setAutomation(s));
    fetchScoreDistribution().then(d => {
      if (d && d.length > 0) setScoreDistribution(d);
    });
  }, [fetchStats, fetchSearchHistory, fetchJobs, fetchAutoApplyCandidates, fetchAutomationStatus, fetchScoreDistribution]);

  const strongMatches = useMemo(() => {
    // Sum counts from score distribution for 75+ ranges
    return scoreDistribution
      .filter(b => b.range === "75-89" || b.range === "90-100")
      .reduce((acc, b) => acc + b.count, 0);
  }, [scoreDistribution]);

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 animate-in">
        <div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            {profile ? `Welcome back${profile.name ? `, ${profile.name.split(' ')[0]}` : ''}` : 'Command Center'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {activePersona?.role_title || 'Your AI job search command center'}
          </p>
        </div>
        <Button data-testid="dash-search-btn" onClick={() => setView("search")}
          className="bg-amber-500 text-black hover:bg-amber-400 rounded-md text-sm font-medium h-9 px-5">
          <MagnifyingGlass size={16} className="mr-2" /> New Search
        </Button>
      </div>

      {/* Automation Status Strip */}
      {automation && (
        <div className="flex items-center gap-5 px-4 py-2 rounded-lg border border-white/5 bg-white/[0.015] mb-6 animate-in" data-testid="automation-strip">
          {[
            { label: "Auto-Score", on: true, detail: "ON" },
            { label: "Radar", on: automation.radar?.enabled, detail: automation.radar?.enabled ? `${automation.radar.interval_hours}h` : "OFF" },
            { label: "Watchlist", on: automation.watchlist?.enabled, detail: automation.watchlist?.enabled ? `${automation.watchlist.active_companies} cos` : "OFF" },
            { label: "Auto-Apply", on: automation.auto_apply?.enabled, detail: automation.auto_apply?.enabled ? `${automation.auto_apply.min_score}%+` : "OFF" },
          ].map(a => (
            <div key={a.label} className="flex items-center gap-1.5" data-testid={`auto-${a.label.toLowerCase()}`}>
              <div className={`w-1.5 h-1.5 rounded-full ${a.on ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-700'}`} />
              <span className="text-[11px] text-zinc-500">{a.label}: <span className={a.on ? 'text-emerald-400' : 'text-zinc-600'}>{a.detail}</span></span>
            </div>
          ))}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="stats-grid">
        {/* JOBS FOUND — with sparkline */}
        <div onClick={() => setView("results")}
          className="jp-stat cursor-pointer hover:border-white/15 transition-all animate-in lg:col-span-2"
          data-testid="stat-jobs-found">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase size={15} style={{ color: "#F59E0B" }} weight="duotone" />
            <span className="stat-label">JOBS FOUND</span>
          </div>
          <div className="stat-value" style={{ color: "#F59E0B" }}>{stats?.total_jobs || 0}</div>
          {(stats?.jobs_over_time || []).length > 1 && (
            <div className="mt-2 -mx-1">
              <ResponsiveContainer width="100%" height={28}>
                <AreaChart data={stats.jobs_over_time}>
                  <Area type="monotone" dataKey="count" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.15} strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        {[
          { label: "STRONG MATCHES", value: strongMatches, color: "#10B981", icon: Target, click: "results" },
          { label: "AUTO-APPLY", value: autoApplyCount, color: "#F59E0B", icon: Rocket, click: "watchlist" },
          { label: "APPLIED", value: stats?.applied || 0, color: "#38BDF8", icon: ArrowRight, click: "pipeline" },
          { label: "INTERVIEWS", value: stats?.interview || 0, color: "#A855F7", icon: Lightning, click: "pipeline" },
          { label: "OFFERS", value: stats?.offer || 0, color: "#10B981", icon: ChartBar, click: "pipeline" },
        ].map(s => (
          <div key={s.label}
            onClick={() => setView(s.click)}
            className="jp-stat cursor-pointer hover:border-white/15 transition-all animate-in"
            data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-3">
              <s.icon size={15} style={{ color: s.color }} weight="duotone" />
              <span className="stat-label">{s.label}</span>
            </div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
        {/* Response Rate card */}
        <div className="jp-stat animate-in" data-testid="stat-response-rate">
          <div className="flex items-center gap-2 mb-3">
            <Percent size={15} style={{ color: "#38BDF8" }} weight="duotone" />
            <span className="stat-label">RESPONSE RATE</span>
          </div>
          <div className="stat-value" style={{ color: "#38BDF8" }}>
            {stats?.pipeline_velocity?.response_rate_pct != null
              ? `${stats.pipeline_velocity.response_rate_pct}%`
              : <span className="text-zinc-700 text-sm font-normal">Not enough data</span>}
          </div>
        </div>
      </div>

      {/* Two Column: Chart + Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Score Distribution — from server aggregation */}
        <div className="lg:col-span-2 jp-card animate-in" data-testid="score-distribution">
          <div className="jp-card-header">
            <span className="jp-label">MATCH SCORE DISTRIBUTION ({stats?.scored_jobs || 0} scored)</span>
            <button onClick={() => setView("results")} className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
              View all <ArrowRight size={10} />
            </button>
          </div>
          <div className="p-5">
            {scoreDistribution.some(b => b.count > 0) ? (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={scoreDistribution} barCategoryGap="20%">
                  <XAxis dataKey="range" tick={{ fill: '#71717A', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} width={25} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer" onClick={() => setView("results")}>
                    {scoreDistribution.map((entry, i) => (
                      <Cell key={i} fill={entry.color} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[170px] flex items-center justify-center text-zinc-600 text-sm">
                <button onClick={() => setView("search")} className="text-amber-500 hover:underline">Search jobs</button>&nbsp;to see distribution
              </div>
            )}
          </div>
        </div>

        {/* Pipeline */}
        <div className="jp-card animate-in" data-testid="pipeline-funnel">
          <div className="jp-card-header">
            <span className="jp-label">PIPELINE</span>
            <button onClick={() => setView("pipeline")} className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
              Open <ArrowRight size={10} />
            </button>
          </div>
          <div className="p-4 space-y-3">
            {[
              { label: "Saved", count: stats?.saved || 0, color: "#71717A", width: "100%" },
              { label: "Applied", count: stats?.applied || 0, color: "#38BDF8", width: "80%" },
              { label: "Interview", count: stats?.interview || 0, color: "#F59E0B", width: "60%" },
              { label: "Offer", count: stats?.offer || 0, color: "#10B981", width: "40%" },
              { label: "Rejected", count: stats?.rejected || 0, color: "#EF4444", width: "30%" },
            ].map(p => (
              <div key={p.label} className="cursor-pointer hover:opacity-80" onClick={() => setView("pipeline")}>
                <div className="flex justify-between mb-1">
                  <span className="text-xs text-zinc-500">{p.label}</span>
                  <span className="font-mono text-xs" style={{ color: p.color }}>{p.count}</span>
                </div>
                <div className="jp-progress-bar">
                  <div className="jp-progress-fill" style={{ width: p.count > 0 ? p.width : '0%', background: p.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Two Column: Top Matches + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Top Matches */}
        <div className="jp-card animate-in" data-testid="top-matches">
          <div className="jp-card-header">
            <span className="jp-label">TOP MATCHES</span>
            <button onClick={() => setView("results")} className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
              View all <ArrowRight size={10} />
            </button>
          </div>
          <div>
            {topJobs.length > 0 ? topJobs.map((job, i) => (
              <div key={job.id}
                onClick={() => setSelectedJob(job)}
                className={`flex items-center gap-3 px-5 py-3 cursor-pointer ${i < topJobs.length - 1 ? 'border-b border-white/5' : ''} hover:bg-white/[0.03] transition-colors`}
                data-testid={`top-job-${job.id}`}>
                <span className="font-mono text-sm font-bold w-10"
                  style={{ color: job.match_score >= 75 ? '#10B981' : job.match_score >= 50 ? '#F59E0B' : '#EF4444' }}>
                  {job.match_score}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{job.title}</div>
                  <div className="text-[11px] text-zinc-600 truncate">{job.company}</div>
                </div>
                {job.job_url && (
                  <a href={job.job_url} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="text-zinc-600 hover:text-amber-500 transition-colors shrink-0">
                    <ArrowSquareOut size={14} />
                  </a>
                )}
              </div>
            )) : (
              <div className="text-center py-8 text-zinc-600 text-sm">
                <button onClick={() => setView("search")} className="text-amber-500 hover:underline">Search jobs</button> to see top matches
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions + Source Breakdown */}
        <div className="space-y-5">
          {/* Quick Actions */}
          <div className="jp-card animate-in" data-testid="quick-actions">
            <div className="jp-card-header">
              <span className="jp-label">QUICK ACTIONS</span>
            </div>
            <div className="grid grid-cols-2 gap-0">
              {[
                { icon: MagnifyingGlass, label: "New Search",     desc: "AI-powered multi-source",                               view: "search",    color: "#F59E0B" },
                { icon: Buildings,       label: "Scan Watchlist", desc: `${automation?.watchlist?.active_companies || 0} companies`, view: "watchlist", color: "#A855F7" },
                { icon: Bell,            label: "Job Radar",      desc: "Auto-discovery alerts",                                 view: "radar",     color: "#EF4444" },
                { icon: Gear,            label: "Resume",         desc: "Upload / update resume",                               view: "resume",    color: "#71717A" },
              ].map((a, i) => (
                <div key={a.label}
                  onClick={() => setView(a.view)}
                  className={`p-4 cursor-pointer hover:bg-white/[0.03] transition-colors border-white/5 ${i < 2 ? 'border-b' : ''} ${i % 2 === 0 ? 'border-r' : ''}`}
                  data-testid={`action-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <a.icon size={18} style={{ color: a.color }} weight="duotone" className="mb-2" />
                  <div className="text-xs text-white font-medium">{a.label}</div>
                  <div className="text-[10px] text-zinc-600">{a.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Source Breakdown */}
          <div className="jp-card animate-in" data-testid="source-breakdown">
            <div className="jp-card-header">
              <span className="jp-label">SOURCE BREAKDOWN</span>
            </div>
            <div className="p-4">
              {stats?.source_breakdown && Object.keys(stats.source_breakdown).length > 0 ? (
                <div className="space-y-2.5">
                  {Object.entries(stats.source_breakdown).sort(([,a],[,b]) => b - a).map(([source, count]) => {
                    const total = stats.total_jobs || 1;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={source} className="flex items-center gap-3 cursor-pointer hover:opacity-80" onClick={() => setView("results")}>
                        <div className={`source-dot source-${source}`} />
                        <span className="text-xs text-zinc-500 w-20 uppercase">{source}</span>
                        <div className="flex-1 jp-progress-bar">
                          <div className="jp-progress-fill" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono text-[11px] text-zinc-400 w-14 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-zinc-600 text-xs">Run a search to see sources</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Searches */}
      {searchHistory.length > 0 && (
        <div className="jp-card animate-in mb-5" data-testid="search-history">
          <div className="jp-card-header">
            <span className="jp-label">RECENT SEARCHES</span>
            <button onClick={() => setView("search")} className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1">
              New search <ArrowRight size={10} />
            </button>
          </div>
          {searchHistory.slice(0, 4).map((h, i) => (
            <div key={h.id}
              onClick={() => setView("search")}
              className={`flex items-center justify-between px-5 py-3 cursor-pointer ${i < 3 ? 'border-b border-white/5' : ''} hover:bg-white/[0.02]`}>
              <div className="flex items-center gap-3 min-w-0">
                <Clock size={14} className="text-zinc-600 shrink-0" />
                <span className="text-sm text-zinc-400 truncate">{h.params?.search_text?.slice(0, 50) || 'Search'}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="jp-badge jp-badge-amber">{h.result_count} jobs</span>
                <span className="text-[10px] text-zinc-600 font-mono">{new Date(h.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
